import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Stack, Avatar, MenuItem, ListItemIcon, ListItemText, IconButton, Divider, Typography, Skeleton, SvgIcon, Menu } from "@mui/material";
import { MENU_ITEMS, ClickAction } from "./config";
import youtubeIcon from "../../../../public/images/youtubeIcon.svg";
import slackIcon from "../../../../public/images/slackIcon.svg";
import { User } from "../../services/types";
import { AppState } from "../../store/types";
import { useGetUsersRolesQuery } from "../../services/accounts";
import { logout } from "../../store/auth";
interface GlobalAccountMenuProps {
  open: boolean;
  anchorEl: HTMLElement;
  onClose: () => void;
  onShowDocsMenu: () => void;
}
export const GlobalAccountMenu: FC<GlobalAccountMenuProps> = ({
  open,
  anchorEl,
  onClose,
  onShowDocsMenu
}) => {
  const {
    t
  } = useTranslation();
  const user: User = useSelector((state: AppState) => state.user);
  const auth = useSelector((state: AppState) => state.auth);
  const {
    data: roles,
    isLoading: isLoadingRoles
  } = useGetUsersRolesQuery();
  const userRole = useMemo(() => {
    return roles?.find(role => role.ZUID === user.ZUID)?.role?.name;
  }, [roles]);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!auth.valid) {
      onClose();
    }
  }, [auth.valid]);
  const handleClickAction = ([action, location]: ClickAction) => {
    switch (action) {
      case "openUrl":
        !!location && handleOpenUrl(location);
        break;
      case "openView":
        onShowDocsMenu();
        break;
      case "openEmail":
        handleOpenUrl("mailto:support@zesty.io");
        break;
      case "logOut":
        dispatch(logout());
        break;
      default:
        break;
    }
  };
  const handleOpenUrl = (url: string) => {
    onClose();
    window.open(url, "_blank", "noopener");
  };
  return <Menu open={open} anchorEl={anchorEl} onClose={onClose} PaperProps={{
    sx: {
      height: 395,
      width: 340
    }
  }} MenuListProps={{
    sx: {
      pt: 0
    }
  }} anchorOrigin={{
    vertical: -12,
    horizontal: "left"
  }} transformOrigin={{
    vertical: "bottom",
    horizontal: "left"
  }} data-sentry-element="Menu" data-sentry-component="GlobalAccountMenu" data-sentry-source-file="index.tsx">
      <Stack direction="row" gap={1.5} py={2} px={2} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="index.tsx">
        <Avatar alt={`${user?.firstName} ${user?.lastName} Avatar`} src={`https://www.gravatar.com/avatar/${user?.emailHash}.jpg?&s=40`} sx={{
        height: 32,
        width: 32
      }} data-sentry-element="Avatar" data-sentry-source-file="index.tsx" />
        <Stack data-sentry-element="Stack" data-sentry-source-file="index.tsx">
          <Typography variant="body2" fontWeight={600} data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            {isLoadingRoles ? <Skeleton /> : userRole}
          </Typography>
        </Stack>
      </Stack>
      <Divider sx={{
      mb: 1
    }} data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
      {MENU_ITEMS.map((menuItem, index) => <MenuItem key={index} onClick={() => handleClickAction(menuItem.action)} data-cy={menuItem.dataCy}>
          <ListItemIcon>
            <SvgIcon component={menuItem.icon} />
          </ListItemIcon>
          <ListItemText>{t(menuItem.textKey)}</ListItemText>
        </MenuItem>)}
      <Divider data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
      <Stack direction="row" gap={1} alignItems="center" px={2} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
        <IconButton size="small" onClick={() => handleOpenUrl("https://www.youtube.com/c/Zestyio/videos")} data-sentry-element="IconButton" data-sentry-source-file="index.tsx">
          <img src={youtubeIcon} alt="youtube" />
        </IconButton>
        <IconButton size="small" onClick={() => handleOpenUrl("https://www.zesty.io/chat/")} data-sentry-element="IconButton" data-sentry-source-file="index.tsx">
          <img src={slackIcon} alt="slack" />
        </IconButton>
      </Stack>
    </Menu>;
};